import type {
  TrackVoteEntry,
  TrackVoteSummary,
} from '@/composables/api/usePlaylistSwitchingApi';

/**
 * Pure display grammar for the track-row vote indicator — no Vue, no icons, so
 * the three densities can't drift and the whole thing is unit-testable without
 * mounting.
 *
 * The colour rule mirrors the legacy ROW rule (GVAdminOSX
 * TrackListItemTableCellView.swift:202-213): a staff `user` vote reads
 * emerald, a client `contact` / external `source` vote reads amber. This is
 * deliberately the row rule, not the popover's `vote_type_id == 6` rule —
 * because the indicator *is* a row.
 */

export type VoteGlyph = 'love' | 'hate' | 'comment' | 'none';
export type VoteTone = 'user' | 'client' | 'muted';

/**
 * The glyph the row shows. Prefers the effective love/hate vote; falls back to
 * a comment glyph when there is no value vote but there are comments, so a
 * comment-only track is never drawn as un-voted.
 */
export function voteGlyphFor(summary: TrackVoteSummary | null | undefined): VoteGlyph {
  if (!summary) {
    return 'none';
  }
  if (summary.effective) {
    return summary.effective.vote_label === 'hate' ? 'hate' : 'love';
  }
  if (summary.counts.comments > 0) {
    return 'comment';
  }
  return 'none';
}

/** The entry whose voter drives the glyph tone/current-user state (the effective one, else the newest). */
export function drivingEntry(
  summary: TrackVoteSummary | null | undefined,
): TrackVoteEntry | null {
  if (!summary) {
    return null;
  }
  if (summary.effective) {
    return summary.effective;
  }
  // Newest entry carrying a comment (entries are already newest-first).
  return summary.entries.find((entry) => entry.comment !== null) ?? summary.entries[0] ?? null;
}

/** Emerald for a staff user, amber for a client/source vote, muted otherwise. */
export function voteToneFor(summary: TrackVoteSummary | null | undefined): VoteTone {
  const entry = drivingEntry(summary);
  if (!entry) {
    return 'muted';
  }
  return entry.voter.kind === 'user' ? 'user' : 'client';
}

/**
 * Whether the driving vote is the current viewer's.
 *
 * Only the comment bubble draws on this — a filled bubble is your comment, an
 * outline one is somebody else's. Hearts and crosses take their fill from the
 * voter's colour instead, so they are always filled when a vote decided them.
 */
export function isOwnVote(summary: TrackVoteSummary | null | undefined): boolean {
  return drivingEntry(summary)?.voter.is_current_user ?? false;
}

/**
 * Which of the legacy icon colours to draw.
 *
 * These are the variants the macOS app has always used, so the colours mean
 * what a playlist manager already expects them to mean:
 *
 *   staff  — green   (the vote was internal, `vote_type_id` 6)
 *   client — orange  (any other vote type: web, clientweb, feedback…)
 *   combo  — the split green-and-orange icon: staff AND a client both voted
 *            the same way
 *   master — purple: the deciding vote belongs to the master zone
 *
 * Purple wins over the split icon. Where the vote came from matters more than
 * who cast it, because a master zone's vote cannot be changed from here.
 *
 * Only votes that AGREE with the deciding vote count towards the split icon —
 * a staff hate next to a client love is not "both hated it".
 */
export type VoteVariant = 'staff' | 'client' | 'combo' | 'master' | 'none';

export function voteVariant(summary: TrackVoteSummary | null | undefined): VoteVariant {
  const deciding = drivingEntry(summary);
  if (!summary || !deciding) {
    return 'none';
  }

  if (deciding.origin === 'master') {
    return 'master';
  }

  const agreeing = summary.entries.filter(
    (entry) => entry.vote_label === deciding.vote_label && entry.excluded_reason !== 'master_gate_off',
  );
  const hasStaff = agreeing.some((entry) => entry.is_internal);
  const hasClient = agreeing.some((entry) => !entry.is_internal);

  if (hasStaff && hasClient) {
    return 'combo';
  }
  return hasStaff ? 'staff' : 'client';
}

/** Whether to show a comment count chip alongside the glyph. */
export function commentCount(summary: TrackVoteSummary | null | undefined): number {
  return summary?.counts.comments ?? 0;
}

/**
 * Whether the deciding vote keeps this track off generated playlists.
 *
 * One hate vote removes a track, whoever cast it and however old it is, so a
 * deciding vote of "hate" IS the exclusion — the API deliberately doesn't send
 * a second field saying the same thing, because two names for one fact drift
 * apart. Mirrors `TrackVoteSummary::excludesTrack()` on the backend.
 */
export function excludesTrack(summary: TrackVoteSummary | null | undefined): boolean {
  return summary?.effective?.vote_label === 'hate';
}

/** Whether there is anything at all to render for this track. */
export function hasVoteIndicator(summary: TrackVoteSummary | null | undefined): boolean {
  return voteGlyphFor(summary) !== 'none' || commentCount(summary) > 0;
}

const GLYPH_LABEL: Record<Exclude<VoteGlyph, 'none'>, string> = {
  love: 'Loved',
  hate: 'Hated',
  comment: 'Comment',
};

const TONE_LABEL: Record<VoteTone, string> = {
  user: 'staff',
  client: 'client',
  muted: '',
};

/**
 * A screen-reader label for the whole indicator — nothing conveyed by colour
 * alone. e.g. "Hated by staff, your vote, 2 comments, excluded by a hate vote".
 */
export function voteAriaLabel(summary: TrackVoteSummary | null | undefined): string {
  const glyph = voteGlyphFor(summary);
  if (glyph === 'none' && commentCount(summary) === 0) {
    return '';
  }

  const parts: string[] = [];
  if (glyph !== 'none') {
    const tone = TONE_LABEL[voteToneFor(summary)];
    parts.push(tone ? `${GLYPH_LABEL[glyph]} by ${tone}` : GLYPH_LABEL[glyph]);
  }
  if (isOwnVote(summary)) {
    parts.push('your vote');
  }
  const comments = commentCount(summary);
  if (comments > 0) {
    parts.push(`${comments} comment${comments === 1 ? '' : 's'}`);
  }
  if (excludesTrack(summary)) {
    parts.push('excluded by a hate vote');
  }
  return parts.join(', ');
}

/**
 * A one-line summary for the popover header: the vote VALUES, then the total
 * number of comments.
 *
 * The two are counted differently and must not be conflated — `comment_only`
 * counts rows whose vote is -1, while `comments` counts every row carrying
 * text (a love or hate vote can have one too). Labelling the former "comments"
 * made the header read "1 comment" above a list of two comment bodies, so the
 * valueless bucket uses the legacy term for it — "neutral"
 * (SIAdminKit TrackVotesViewModel.swift:178,194) — and the comment total is
 * reported separately, matching what the list below actually shows.
 */
export function summarizeCounts(summary: TrackVoteSummary | null | undefined): string {
  if (!summary) {
    return 'No votes';
  }
  const { love, hate, comment_only: commentOnly, comments } = summary.counts;
  const parts: string[] = [];
  if (love > 0) parts.push(`${love} love`);
  if (hate > 0) parts.push(`${hate} hate`);
  if (commentOnly > 0) parts.push(`${commentOnly} neutral`);
  if (comments > 0) parts.push(`${comments} comment${comments === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(' · ') : 'No votes';
}
